import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-14 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    'kexue-renzhi': '科学认知',
    'pinlv-jiankang': '频率与健康',
    'xinli-yingxiang': '心理影响',
    'changjian-wuqu': '常见误区',
    'xing-jiaoyu': '性教育',
    'xingwei-guanli': '行为管理',
  };

  const catName = categoryMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);
  const moreArticles = articles
    .filter((a) => a.category !== article.category)
    .slice(0, 3);

  const siteUrl = 'https://bodymindapp.github.io';
  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    dateModified: article.date,
    url: articleUrl,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '健康认知指南',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 手淫的科学认知与健康指南`}
        description={article.excerpt}
        keywords={`手淫,${article.tags.join(',')},${catName},性健康教育`}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={articleSchema}
      />
      <Navbar />
      <main className="pt-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: catName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-8 md:py-12 px-4 md:px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-4">
                {catName}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>

            {moreArticles.length > 0 && (
              <>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 mt-12">
                  更多推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {moreArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}